import { FC, useEffect, useState, useRef } from "react";
import Input from "shared/Input/Input";
import Select from "shared/Select/Select";
import CommonLayout from "./CommonLayout";
import FormItem from "./FormItem";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentHost, setHost } from "application/features/host/hostSlice";
import { useNavigate } from "react-router-dom";
import { useUpdateHostMutation } from "application/features/host/hostApiSlice";
import { regionArray } from "data/geography";
import AddressInputMiniMap from "components/GoogleMaps/AddressInputMiniMap";
import { PlaceAutocomplete } from "components/GoogleMaps/PlaceAutocomplete";
import type { PlaceResult } from "components/GoogleMaps/PlaceAutocomplete";

export interface PageAddHostInfoProps { }

const PageAddHostInfo3: FC<PageAddHostInfoProps> = () => {

  const host = useSelector(selectCurrentHost);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [updateHost, { isLoading: isUpdating }] = useUpdateHostMutation();

  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | null>({
    latitude: host?.location?.latitude || 41.9027,
    longitude: host?.location?.longitude || 12.4963,
    city: host?.city || "",
    country: host?.state || "",
    province: host?.country || "",
    postal_code: host?.postal_code || "",
    formatted_address: host?.address || "",
  });

  const [canGoNext, setCanGoNext] = useState<boolean>(false)

  const handleUpdateHost = (field: string, value: any) => {
    try {
      dispatch(setHost({ ...host, [field]: value }));
    } catch (e) {
      console.log(e);
    }
  };

  const cityInputRef = useRef<HTMLInputElement>(null);
  const countryInputRef = useRef<HTMLInputElement>(null);
  const postalCodeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {

    if (!selectedPlace) {
      return;
    }

    const updatedHost = {
      ...host,
      location: {
        latitude: selectedPlace.latitude,
        longitude: selectedPlace.longitude,
      },
      city: selectedPlace.city,
      state: selectedPlace.country,
      postal_code: selectedPlace.postal_code,
      address: selectedPlace.formatted_address,
      country: selectedPlace.province,
    };
    dispatch(setHost(updatedHost));
  }, [selectedPlace]);

  const _updateDefaultFields = () => {
    dispatch(setHost({
      ...host,
      state: "italia",
    }));
  }

  const onSubmit = async () => {
    try {
      await updateHost(host).unwrap();
      navigate("/host")
    } catch (err) {
      alert("Errore durante l'aggiornamento dei dati")
    }
  }

  useEffect(() => {
    _updateDefaultFields()
  }, []);

  useEffect(() => {
    if (host.address && host.city && host.country && host.postal_code) {
      setCanGoNext(true)
    } else {
      setCanGoNext(false)
    }
  }, [host]);

  return (
    <CommonLayout
      index="03"
      nextHref={canGoNext ? "/host/info/upload" : ""}
      backtHref="/host/info/add-info-2"
      canGoNext={canGoNext}
      onNext={onSubmit}
    >
      <>
        <h2 className="text-2xl font-semibold">I tuoi dati</h2>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
        {/* FORM */}
        <div className="space-y-8">
          {/* ITEM */}
          <FormItem
            label="Nazione"
          >
            <Select
              defaultValue={host.state}
              onChange={(e) => handleUpdateHost("state", e.target.value)}
              placeholder="Nazione"
            >
              {
                regionArray.map((item) => <option value={item[0]}>{item[0]}</option>)
              }
            </Select>
          </FormItem>
          <FormItem
            label="Indirizzo"
          >
            <PlaceAutocomplete
              onPlaceSelect={setSelectedPlace}
              placeholder="Cerca un indirizzo"
            />
          </FormItem>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-5">
            <FormItem
              label="Città"
            >
              <Input
                defaultValue={host.city}
                onChange={(e) => handleUpdateHost("city", e.target.value)}
                placeholder="Città"
                value={host.city || ""}
                ref={cityInputRef}
              />
            </FormItem>
            <FormItem
              label="Provincia"
            >
              <Input
                defaultValue={host.country}
                onChange={(e) => handleUpdateHost("country", e.target.value)}
                placeholder="Provincia"
                ref={countryInputRef}
                value={host.country || ""}
              />
            </FormItem>
            <FormItem
              label="Codice postale"
            >
              <Input
                defaultValue={host.postal_code}
                onChange={(e) => handleUpdateHost("postal_code", e.target.value)}
                placeholder="Codice postale"
                value={host.postal_code || ""}
                ref={postalCodeInputRef}
              />
            </FormItem>
          </div>
          <div>
            <div className="mt-4">
              <div className="aspect-w-5 aspect-h-5 sm:aspect-h-3">
                <AddressInputMiniMap
                  className="mt-4"
                  defaultCenter={{ lat: selectedPlace.latitude, lng: selectedPlace.longitude }}
                  place={selectedPlace}
                />
              </div>
            </div>
          </div>
        </div>
      </>
    </CommonLayout >
  );
};

export default PageAddHostInfo3;
